import { apiFetch } from '@/lib/api';

export type SupportTicketReason = 'question' | 'problem' | 'suggestion' | 'other';
export type SupportTicketStatus = 'open' | 'in_analysis' | 'closed';

/**
 * F161 — the description cap shared by the create-form zod schema and the
 * `CharCounter` so the two can never drift apart from the backend's 1..1000
 * validation on `support_tickets.description`.
 */
export const SUPPORT_DESCRIPTION_MAX = 1000;

/** Auto-captured, invisible to the tenant — currently just the route the
 *  ticket was opened from. See FR3 "context: {route}". */
export interface SupportTicketContext {
  route?: string;
}

/**
 * F162 FR4 — a reply on a tenant's own ticket. Only ever a **sent** message:
 * the tenant-facing repository/serializer filters `state='sent'` server-side
 * and never exposes `state` at all, so drafts (including AI-agent drafts
 * awaiting admin approval) never leak here.
 */
export type SupportMessageAuthorType = 'tenant_user' | 'admin' | 'ai_agent';

export interface SupportTicketMessage {
  id: number;
  body: string;
  author_type: SupportMessageAuthorType;
  created_at: string;
  sent_at: string | null;
}

export interface SupportTicket {
  id: number;
  reason: SupportTicketReason;
  description: string;
  status: SupportTicketStatus;
  created_at: string;
  /**
   * F162 FR4 — sent replies only, oldest first. The ticket's own
   * `description` is NEVER mirrored as a message here — it stays only on
   * this row; the thread UI renders it as the first bubble from `description`
   * itself, never duplicating it out of `messages`.
   */
  messages: SupportTicketMessage[];
}

export interface CreateSupportTicketRequest {
  reason: SupportTicketReason;
  description: string;
  context?: SupportTicketContext;
}

export const supportService = {
  /** Own-tenant tickets, newest first (per the API contract — no pagination). */
  list(): Promise<SupportTicket[]> {
    return apiFetch<SupportTicket[]>('/support/tickets');
  },
  create(payload: CreateSupportTicketRequest): Promise<SupportTicket> {
    return apiFetch<SupportTicket>('/support/tickets', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};
